(() => {
  const FETCH_TIMEOUT_MS = 6000;

  const extractSlidesFromSectionHtml = (html, sectionId) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section =
      (sectionId ? doc.getElementById(`shopify-section-${sectionId}`) : null) ||
      doc.querySelector(".shopify-section");

    if (!section) {
      // Recommendations endpoint may return the section body without a wrapper.
      return [
        ...doc.querySelectorAll(".swiper-slide.product-carousel-panel__slide"),
      ];
    }

    return [
      ...section.querySelectorAll(
        ".swiper-slide.product-carousel-panel__slide",
      ),
    ];
  };

  const hideEmptyCarouselIfNeeded = (carouselRoot) => {
    const panels = [
      ...carouselRoot.querySelectorAll("[data-product-carousel-panel]"),
    ].filter(
      (panel) => !panel.classList.contains("product-carousel-panel--empty"),
    );

    const visiblePanels = panels.filter((panel) => {
      if (panel.hidden) return false;

      const slides = panel.querySelectorAll(
        ".swiper-slide.product-carousel-panel__slide",
      );

      return slides.length > 0;
    });

    if (visiblePanels.length > 0) return;

    const section = carouselRoot.closest(".product-carousel-section");

    if (section) {
      section.hidden = true;
      return;
    }

    carouselRoot.hidden = true;
  };

  const fetchWithTimeout = async (url, { signal, headers, timeoutMs }) => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(
      () => timeoutController.abort(),
      timeoutMs ?? FETCH_TIMEOUT_MS,
    );

    const onParentAbort = () => timeoutController.abort();
    signal?.addEventListener("abort", onParentAbort, { once: true });

    try {
      return await fetch(url, {
        signal: timeoutController.signal,
        headers,
      });
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onParentAbort);
    }
  };

  class ProductCarouselDynamicPanels {
    #abortController = null;

    constructor(carouselRoot) {
      this.#abortController = new AbortController();
      this.root = carouselRoot;
    }

    abort() {
      this.#abortController?.abort();
      this.#abortController = null;
    }

    async hydrate({ onPanelUpdated }) {
      const controller = this.#abortController;
      if (!controller || !this.root.isConnected) return;

      const { signal } = controller;
      const tasks = [
        ...this.root.querySelectorAll(
          '[data-product-carousel-source="recommendations"]',
        ),
      ].map((panel) =>
        this.#loadRecommendationsPanel(panel, signal, onPanelUpdated),
      );

      const recentPanels = [
        ...this.root.querySelectorAll(
          '[data-product-carousel-source="recently_viewed"]',
        ),
      ];

      for (const panel of recentPanels) {
        tasks.push(
          this.#loadRecentlyViewedPanel(panel, signal, onPanelUpdated),
        );
      }

      await Promise.all(tasks);

      if (!signal.aborted && this.root.isConnected) {
        hideEmptyCarouselIfNeeded(this.root);
      }
    }

    async #loadRecommendationsPanel(panel, signal, onPanelUpdated) {
      const baseUrl = panel.dataset.recommendationsUrl;
      // Off-PDP (e.g. PLP) there is no current product — seed the
      // recommendations request from the most recent recently-viewed product.
      const productId = panel.dataset.productId || this.#recentlyViewedSeedId();
      const limit = panel.dataset.productsLimit || "12";
      const sectionId =
        panel.dataset.recommendationsSectionId ||
        "product-carousel-recommendations";

      if (!baseUrl || !productId) {
        this.#finishWithExistingSlidesOrHide(panel, onPanelUpdated);
        return;
      }

      // Prefer `sections=` (JSON map of section HTML). `section_id=` returns raw
      // HTML on some hosts but 404s through theme preview / primary-domain proxy.
      const requestUrl = new URL(baseUrl, window.location.origin);
      requestUrl.searchParams.set("product_id", productId);
      requestUrl.searchParams.set("limit", limit);
      requestUrl.searchParams.set("intent", "related");
      requestUrl.searchParams.set("sections", sectionId);

      try {
        // Do not send Accept: application/json — that returns the products
        // payload and drops section HTML. text/html still yields a JSON map
        // of rendered sections when `sections=` is present.
        const response = await fetchWithTimeout(requestUrl.toString(), {
          signal,
          headers: { Accept: "text/html" },
        });

        if (!response.ok) {
          this.#finishWithExistingSlidesOrHide(panel, onPanelUpdated);
          return;
        }

        const raw = await response.text();
        let html = raw;

        try {
          const payload = JSON.parse(raw);
          html =
            typeof payload === "string"
              ? payload
              : payload?.[sectionId] || payload?.sections?.[sectionId] || "";
        } catch {
          // Raw HTML from section_id-style responses.
        }

        if (!html) {
          this.#finishWithExistingSlidesOrHide(panel, onPanelUpdated);
          return;
        }

        const slides = extractSlidesFromSectionHtml(html, sectionId);

        if (slides.length === 0) {
          this.#finishWithExistingSlidesOrHide(panel, onPanelUpdated);
          return;
        }

        this.#appendSlides(panel, slides, onPanelUpdated);
      } catch (error) {
        // Parent disconnect abort — bail. Per-request timeouts still fall through
        // so SSR bestsellers can surface.
        if (signal?.aborted) return;

        this.#finishWithExistingSlidesOrHide(panel, onPanelUpdated);
      }
    }

    #recentlyViewedSeedId() {
      if (!window.RokaRecentlyViewed?.read) return "";

      const [mostRecent] = window.RokaRecentlyViewed.read();

      return mostRecent?.id ? String(mostRecent.id) : "";
    }

    async #loadRecentlyViewedPanel(panel, signal, onPanelUpdated) {
      const currentProductId = panel.dataset.currentProductId || "";
      const limit = Number.parseInt(panel.dataset.productsLimit || "12", 10);
      const cardSectionId =
        panel.dataset.cardSectionId || "product-carousel-card";

      if (!window.RokaRecentlyViewed?.read) {
        this.#hidePanel(panel, onPanelUpdated);
        return;
      }

      const entries = window.RokaRecentlyViewed.read().filter(
        (item) => String(item.id) !== String(currentProductId) && item.handle,
      );

      if (entries.length === 0) {
        this.#hidePanel(panel, onPanelUpdated);
        return;
      }

      const handles = entries.slice(0, limit).map((item) => item.handle);
      const previewThemeId = new URLSearchParams(window.location.search).get(
        "preview_theme_id",
      );

      const slideGroups = await Promise.all(
        handles.map((handle) =>
          this.#fetchRecentlyViewedSlides(
            handle,
            cardSectionId,
            previewThemeId,
            signal,
          ),
        ),
      );

      if (signal.aborted) return;

      const slides = slideGroups.flat();

      if (slides.length === 0) {
        this.#hidePanel(panel, onPanelUpdated);
        return;
      }

      this.#appendSlides(panel, slides, onPanelUpdated);
    }

    async #fetchRecentlyViewedSlides(
      handle,
      cardSectionId,
      previewThemeId,
      signal,
    ) {
      if (signal.aborted) return [];

      try {
        const requestUrl = new URL(
          `/products/${handle}`,
          window.location.origin,
        );
        // Prefer `sections=` (JSON map). `section_id=` 404s through theme
        // preview / primary-domain proxy — same pattern as recommendations.
        requestUrl.searchParams.set("sections", cardSectionId);

        if (previewThemeId) {
          requestUrl.searchParams.set("preview_theme_id", previewThemeId);
        }

        const response = await fetchWithTimeout(requestUrl.toString(), {
          signal,
          headers: { Accept: "text/html" },
        });

        if (!response.ok) return [];

        const raw = await response.text();
        let html = raw;

        try {
          const payload = JSON.parse(raw);
          html =
            typeof payload === "string"
              ? payload
              : payload?.[cardSectionId] ||
                payload?.sections?.[cardSectionId] ||
                "";
        } catch {
          // Raw HTML from older section_id-style responses.
        }

        if (!html) return [];

        return extractSlidesFromSectionHtml(html, cardSectionId);
      } catch (error) {
        if (error?.name === "AbortError") return [];

        return [];
      }
    }

    #appendSlides(panel, slides, onPanelUpdated) {
      const track = panel.querySelector(".swiper-wrapper");

      if (!track) {
        this.#hidePanel(panel, onPanelUpdated);
        return;
      }

      track.replaceChildren(
        ...slides.map((slide) => document.importNode(slide, true)),
      );
      this.#revealPanel(panel, onPanelUpdated);
    }

    #panelHasSlides(panel) {
      return (
        panel.querySelectorAll(".swiper-slide.product-carousel-panel__slide")
          .length > 0
      );
    }

    // Keep SSR bestsellers/collection fallback when recommendations fetch
    // fails or returns nothing — only hide when the panel is truly empty.
    #finishWithExistingSlidesOrHide(panel, onPanelUpdated) {
      if (this.#panelHasSlides(panel)) {
        this.#revealPanel(panel, onPanelUpdated);
        return;
      }

      this.#hidePanel(panel, onPanelUpdated);
    }

    #revealPanel(panel, onPanelUpdated) {
      panel.hidden = false;
      panel.classList.remove("product-carousel-panel--pending");
      this.#revealPanelChrome(panel);
      onPanelUpdated?.(panel);
    }

    #revealPanelChrome(panel) {
      const tabId = panel.dataset.productCarouselPanel;
      const carousel = panel.closest("product-carousel");

      if (!carousel || !tabId) return;

      const tab = carousel.querySelector(
        `[data-product-carousel-tab="${tabId}"]`,
      );
      const shopLink = carousel.querySelector(
        `[data-product-carousel-shop-link="${tabId}"]`,
      );
      const mobileCta = carousel.querySelector(
        `[data-product-carousel-mobile-cta="${tabId}"]`,
      );

      if (tab) tab.hidden = false;
      if (shopLink) shopLink.hidden = false;
      if (mobileCta) mobileCta.hidden = false;

      const blockWrapper = panel.closest(".shopify-block");

      if (blockWrapper) {
        blockWrapper.hidden = false;
      }
    }

    #hidePanel(panel, onPanelUpdated) {
      panel.hidden = true;
      panel.classList.add("product-carousel-panel--empty");
      panel.classList.remove("product-carousel-panel--pending");

      const blockWrapper = panel.closest(".shopify-block");

      if (blockWrapper) {
        blockWrapper.hidden = true;
      }

      const tabId = panel.dataset.productCarouselPanel;
      const carousel = panel.closest("product-carousel");

      if (!carousel || !tabId) {
        onPanelUpdated?.(panel);
        return;
      }

      const tab = carousel.querySelector(
        `[data-product-carousel-tab="${tabId}"]`,
      );
      const shopLink = carousel.querySelector(
        `[data-product-carousel-shop-link="${tabId}"]`,
      );
      const mobileCta = carousel.querySelector(
        `[data-product-carousel-mobile-cta="${tabId}"]`,
      );

      tab?.remove();
      shopLink?.remove();
      mobileCta?.remove();
      onPanelUpdated?.(panel);
    }
  }

  window.ProductCarouselDynamicPanels = ProductCarouselDynamicPanels;
})();
